import { persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";

import { addCartItem } from "@/actions/zustand-stores/add-cart-item";
import { clearCart } from "@/actions/zustand-stores/clear-cart";
import { ValidCartItem } from "@/actions/zustand-stores/get-cart-data";
import { removeProductWithSize } from "@/actions/zustand-stores/remove-product-with-size";
import { removeProducts } from "@/actions/zustand-stores/remove-products";
import { GetCartResponse } from "@/app/api/cart/route";

type UserId = string | undefined;

export type CartProduct = {
  cart_id: number;
  product_id: string;
  quantity: number;
  product_size: string | null;
  added_at: string | null;
  products: {
    product_id: string;
    slug: string | null;
    name: string;
    avg_price: number;
    image_url: string;
  } | null;
  product_inventory: {
    product_inventory_id: number | null;
    price: number | null;
  } | null;
};

export type CartItem = {
  info: CartProduct;
  quantity: number;
};

export type CartState = {
  products: ValidCartItem[];
  cartItemsCount: number; // The total number of items in the cart
  cartSubtotal: number; // The total price of all items in the cart
  cartShipping: number; // The estimated shipping cost
  cartTax: number; // The estimated tax cost
  cartTotalAmountUsd: number; // The total price of all items in the cart in USD
  isLoading: boolean;
};

export type CartActions = {
  fetchCart: (userId: UserId) => Promise<void>;
  addItem: (userId: UserId, cartItem: CartItem) => Promise<void>;
  removeItem: (
    userId: UserId,
    productId: string,
    size: string,
  ) => Promise<void>;
  removeAll: (userId: UserId) => Promise<void>;
  calculateCartCount: () => void;
  setCartProducts: (products: CartState["products"]) => void;
  setCartSubtotal: (subtotal: CartState["cartSubtotal"]) => void;
  setCartShipping: (shipping: CartState["cartShipping"]) => void;
  setCartTax: (tax: CartState["cartTax"]) => void;
  setCartTotalAmountUsd: (total: CartState["cartTotalAmountUsd"]) => void;
};

export type CartStore = CartState & CartActions;

export const initCartStore = (): CartState => {
  return {
    products: [],
    cartItemsCount: 0,
    cartSubtotal: 0,
    cartShipping: 0,
    cartTax: 0,
    cartTotalAmountUsd: 0,
    isLoading: false,
  };
};

export const defaultInitState: CartState = {
  products: [],
  cartItemsCount: 0,
  cartSubtotal: 0,
  cartShipping: 0,
  cartTax: 0,
  cartTotalAmountUsd: 0,
  isLoading: false,
};

export const createCartStore = (initState: CartState = defaultInitState) => {
  return createStore<CartStore>()(
    persist(
      (set, get) => ({
        ...initState,
        setCartProducts: (products) => {
          const cartItemsCount = products.reduce(
            (acc, item) => acc + item.quantity,
            0,
          );

          // Calculate subtotal
          const cartSubtotal = products.reduce((sum, item) => {
            return sum + item.quantity * item.product_inventory.price;
          }, 0);

          // Calculate shipping
          const cartShipping = cartSubtotal > 250 ? 0 : 15.0;

          // Calculate tax
          const cartTax = cartSubtotal * 0.07;

          // Calculate total amount in USD
          const cartTotalAmountUsd = cartSubtotal + cartShipping + cartTax;

          console.log(
            "CART DATA",

            products,
            cartItemsCount,
            cartSubtotal,
            cartShipping,
            cartTax,
            cartTotalAmountUsd,
          );

          set(() => ({
            products,
            cartItemsCount,
            cartSubtotal,
            cartShipping,
            cartTax,
            cartTotalAmountUsd,
          }));
        },
        // TODO: update the state manually instead of doing a fetch
        fetchCart: async (userId) => {
          if (!userId) return;
          set({ isLoading: true });
          const response = await fetch("/api/cart");
          const { cartData, error }: GetCartResponse = await response.json();

          if (error) {
            console.error("Error fetching cart data:", error);
            set({ isLoading: false });
            return;
          }

          if (cartData) {
            const products = cartData || [];
            const cartItemsCount = products.reduce(
              (acc, item) => acc + item.quantity,
              0,
            );

            // Calculate subtotal
            const cartSubtotal = products.reduce((sum, item) => {
              return sum + item.quantity * item.product_inventory.price;
            }, 0);

            // Calculate shipping
            const cartShipping = cartSubtotal > 250 ? 0 : 15.0;

            // Calculate tax
            const cartTax = cartSubtotal * 0.07;

            // Calculate total amount in USD
            const cartTotalAmountUsd = cartSubtotal + cartShipping + cartTax;

            set({
              products,
              cartItemsCount,
              cartSubtotal,
              cartShipping,
              cartTax,
              cartTotalAmountUsd,
              isLoading: false,
            });
          } else {
            set({ isLoading: false });
          }
        },
        addItem: async (userId, cartItem) => {
          if (!userId) return;
          set({ isLoading: true });

          const { data, error } = await addCartItem(cartItem);

          if (error) {
            console.error("Error adding item to cart:", error);
            set({ isLoading: false });
            return;
          }

          if (data) {
            set((state) => {
              const updatedProducts = [...state.products, data];

              // Recalculate cartItemsCount
              const cartItemsCount = updatedProducts.reduce(
                (acc, item) => acc + item.quantity,
                0,
              );

              // Recalculate cartSubtotal
              const cartSubtotal = updatedProducts.reduce((sum, item) => {
                return sum + item.quantity * item.product_inventory.price;
              }, 0);

              // Recalculate cartShipping
              const cartShipping = cartSubtotal > 250 ? 0 : 15.0;

              // Recalculate cartTax
              const cartTax = cartSubtotal * 0.07;

              // Recalculate cartTotalAmountUsd
              const cartTotalAmountUsd = cartSubtotal + cartShipping + cartTax;

              return {
                products: updatedProducts,
                cartItemsCount,
                cartSubtotal,
                cartShipping,
                cartTax,
                cartTotalAmountUsd,
                isLoading: false,
              };
            });
          } else {
            set({ isLoading: false });
          }
        },
        removeItem: async (userId, productId, size) => {
          if (!userId) return;
          set({ isLoading: true });

          let error;
          if (size) {
            ({ error } = await removeProductWithSize(productId, size));
          } else {
            ({ error } = await removeProducts(productId));
          }

          if (error) {
            console.error("Error removing item:", error);
            set({ isLoading: false });
            return;
          }

          // Fetch updated cart data
          await get().fetchCart(userId);

          // Retrieve updated products
          const products = get().products;

          // Recalculate cartItemsCount
          const cartItemsCount = products.reduce(
            (acc, item) => acc + item.quantity,
            0,
          );

          // Recalculate cartSubtotal
          const cartSubtotal = products.reduce((sum, item) => {
            return sum + item.quantity * item.product_inventory.price;
          }, 0);

          // Recalculate cartShipping
          const cartShipping = cartSubtotal > 250 ? 0 : 15.0;

          // Recalculate cartTax
          const cartTax = cartSubtotal * 0.07;

          // Recalculate cartTotalAmountUsd
          const cartTotalAmountUsd = cartSubtotal + cartShipping + cartTax;

          set({
            cartItemsCount,
            cartSubtotal,
            cartShipping,
            cartTax,
            cartTotalAmountUsd,
            isLoading: false,
          });
        },
        removeAll: async (userId) => {
          if (!userId) return;
          set({ isLoading: true });
          const { error } = await clearCart();

          if (error) {
            console.error("Error removing all items:", error);
            set({ isLoading: false });
            return;
          }

          set({ products: [], isLoading: false });
          get().calculateCartCount();
        },
        // Calculate the total quantity of items in the cart
        calculateCartCount: () => {
          const total = get().products.reduce(
            (sum, item) => sum + item.quantity,
            0,
          );
          set({ cartItemsCount: total });
        },
        setCartSubtotal: (subtotal) => set(() => ({ cartSubtotal: subtotal })),
        setCartShipping: (shipping) => set(() => ({ cartShipping: shipping })),
        setCartTax: (tax) => set(() => ({ cartTax: tax })),
        setCartTotalAmountUsd: (total) =>
          set(() => ({ cartTotalAmountUsd: total })),
      }),
      {
        name: "cart-store",
      },
    ),
  );
};
